import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  ElementRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { AddressInput } from '../../../interfaces/card';
import { GoogleAddressService } from '../../../services/google-address.service';
import { ADDRESS_PLACE_FIELDS, addressFromPlace, formatAddress, toAddressInput } from '../address.utils';

interface AddressRow {
  /** Text in the search input */
  text: string;
  /** Address picked from the suggestions; `null` while the text is not confirmed by Google */
  address: AddressInput | null;
  /** Prefilled from Google, editable because the picked place may have no house number */
  building: string;
  apartment: string;
  touched: boolean;
}

/** Attaches Google Places autocomplete to an input */
@Directive({
  selector: 'input[appBoardV3PlaceAutocomplete]',
  standalone: false,
})
export class BoardV3PlaceAutocompleteDirective implements AfterViewInit, OnDestroy {
  @Output() placeSelected = new EventEmitter<google.maps.places.PlaceResult>();

  private readonly elementRef = inject<ElementRef<HTMLInputElement>>(ElementRef);
  private readonly googleAddressService = inject(GoogleAddressService);
  private readonly ngZone = inject(NgZone);
  private listener?: google.maps.MapsEventListener;
  private destroyed = false;

  ngAfterViewInit(): void {
    this.googleAddressService.api
      .then((maps) => {
        if (this.destroyed) return;

        const autocomplete = new maps.places.Autocomplete(this.elementRef.nativeElement, {
          fields: ADDRESS_PLACE_FIELDS,
          types: ['address'],
        });
        this.listener = autocomplete.addListener('place_changed', () => {
          this.ngZone.run(() => this.placeSelected.emit(autocomplete.getPlace()));
        });
      })
      .catch(() => undefined);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.listener?.remove();
  }
}

/**
 * Editable list of addresses. The value is `AddressInput[]` with confirmed addresses only;
 * a row with typed but unconfirmed text makes the control invalid (`addressPending`).
 */
@Component({
  selector: 'app-board-v3-address-editor',
  templateUrl: './address-editor.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block' },
  providers: [
    { provide: NG_VALUE_ACCESSOR, multi: true, useExisting: forwardRef(() => BoardV3AddressEditorComponent) },
    { provide: NG_VALIDATORS, multi: true, useExisting: forwardRef(() => BoardV3AddressEditorComponent) },
  ],
})
export class BoardV3AddressEditorComponent implements ControlValueAccessor, Validator {
  @Input() size: 'default' | 'small' = 'default';
  /** Keeps one empty row when the list is empty */
  @Input() keepEmptyRow = true;
  /** One address at most (a warehouse): no button to add another row */
  @Input() single = false;

  private readonly cdr = inject(ChangeDetectorRef);

  protected rows: AddressRow[] = [];
  protected disabled = false;
  protected readonly formatAddress = formatAddress;

  private onChange: (value: AddressInput[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: AddressInput[] | null | undefined): void {
    this.rows = (value ?? []).map((address) => ({
      text: formatAddress({ ...address, apartment: null }),
      address: toAddressInput({ ...address, apartment: null }),
      building: address.building ?? '',
      apartment: address.apartment ?? '',
      touched: false,
    }));
    this.ensureRow();
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: AddressInput[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cdr.markForCheck();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    return this.rows.some((row) => this.isPending(row)) ? { addressPending: true } : null;
  }

  /** Border box of the search field; the icon and the input sit side by side inside it */
  protected getFieldClass(invalid: boolean): string {
    const height = this.size === 'small' ? 'h-9' : 'h-10';
    const border = invalid ? 'border-red-400' : 'border-slate-200 focus-within:border-orange-400';
    const background = this.disabled ? 'cursor-not-allowed bg-slate-50' : 'cursor-text bg-white';
    return `${height} ${border} ${background} flex min-w-0 flex-1 items-center gap-2 rounded-lg border px-3`;
  }

  protected getInputClass(layout: string, invalid = false): string {
    const height = this.size === 'small' ? 'h-9' : 'h-10';
    const border = invalid ? 'border-red-400' : 'border-slate-200 focus:border-orange-400';
    return `${layout} ${height} ${border} min-w-0 rounded-lg border bg-white text-sm font-medium text-slate-800 outline-none disabled:cursor-not-allowed disabled:bg-slate-50`;
  }

  protected isPending(row: AddressRow): boolean {
    return !!row.text.trim() && !row.address;
  }

  protected hasError(row: AddressRow): boolean {
    return row.touched && this.isPending(row);
  }

  protected onTextInput(row: AddressRow, event: Event): void {
    row.text = (event.target as HTMLInputElement).value;
    // Any edit after picking a suggestion makes the address unconfirmed again
    row.address = null;
    this.emit();
  }

  protected onPlaceSelected(row: AddressRow, place: google.maps.places.PlaceResult): void {
    const address = addressFromPlace(place);
    row.address = address ? { ...address, apartment: null } : null;
    row.text = address ? formatAddress(row.address) : (place.formatted_address ?? place.name ?? row.text);
    row.building = address?.building ?? '';
    if (address?.apartment && !row.apartment.trim()) row.apartment = address.apartment;
    row.touched = true;
    this.emit();
  }

  protected onBuildingInput(row: AddressRow, event: Event): void {
    row.building = (event.target as HTMLInputElement).value;
    this.emit();
  }

  protected onApartmentInput(row: AddressRow, event: Event): void {
    row.apartment = (event.target as HTMLInputElement).value;
    this.emit();
  }

  protected onBlur(row: AddressRow): void {
    row.touched = true;
    this.onTouched();
  }

  protected addRow(): void {
    this.rows = [...this.rows, this.createRow()];
  }

  protected removeRow(index: number): void {
    this.rows = this.rows.filter((_, rowIndex) => rowIndex !== index);
    this.ensureRow();
    this.emit();
    this.onTouched();
  }

  protected canRemove(row: AddressRow): boolean {
    return this.rows.length > 1 || !this.keepEmptyRow || !!row.text || !!row.apartment;
  }

  private emit(): void {
    this.onChange(this.rows.filter((row) => row.address).map((row) => this.toValue(row, row.address!)));
  }

  /**
   * A building changed by hand no longer matches the Google place, so its formatted text,
   * id and coordinates are dropped and the address is shown from its parts
   */
  private toValue(row: AddressRow, address: AddressInput): AddressInput {
    const building = row.building.trim();
    const isBuildingEdited = building !== (address.building ?? '');
    const placeData = isBuildingEdited
      ? { formatted_address: null, place_id: null, latitude: null, longitude: null }
      : {};

    return toAddressInput({ ...address, ...placeData, building, apartment: row.apartment });
  }

  private ensureRow(): void {
    if (!this.rows.length && this.keepEmptyRow) this.rows = [this.createRow()];
  }

  private createRow(): AddressRow {
    return { text: '', address: null, building: '', apartment: '', touched: false };
  }
}
